import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Loader2, Plus, Trash2, ImagePlus, X, LayoutTemplate } from "lucide-react";
import { api } from "../../lib/api";
import { handlePdfError } from "../../lib/pdf";
import { useAuth } from "../../context/AuthContext";
import { opCost, creditLabel, tierName } from "../../lib/pricing";
import { useOnline } from "../../hooks/usePWA";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import BrandingBar from "./BrandingBar";
import ToolIntro from "./ToolIntro";
import PreviewUnlock from "./PreviewUnlock";

const DEMO = {
  invoice: {
    invoice_number: "INV-1042", from_name: "Bright Studio", from_details: "hello@brightstudio.com\n123 Market St",
    to_name: "Acme Corp", to_details: "billing@acme.com", date: "2026-06-01", due_date: "2026-06-15",
    currency: "$", tax_rate: 8, logo: "",
    items: [
      { description: "Website design", qty: 1, price: 1200 },
      { description: "Copywriting (hours)", qty: 6, price: 90 },
    ],
    notes: "Payment due within 14 days. Thank you for your business!",
  },
  resume: {
    name: "Jordan Rivera", headline: "Senior Product Designer", contact: "jordan@email.com · +1 555 0134 · San Francisco",
    summary: "Product designer with 8+ years crafting intuitive digital experiences for startups and enterprises.",
    experience: [
      { role: "Senior Product Designer", company: "Nimbus", period: "2021 — Present", summary: "Led design for the core platform used by 2M+ users." },
      { role: "Product Designer", company: "Loop", period: "2018 — 2021", summary: "Owned end-to-end design of the mobile app." },
    ],
    education: [{ degree: "B.A. Design", school: "UC Berkeley", period: "2014 — 2018" }],
    skills: "Figma, Prototyping, Design Systems, User Research, HTML/CSS",
  },
  certificate: {
    type: "Achievement", recipient: "Samantha Lee", logo: "",
    description: "In recognition of outstanding performance and dedication in completing the Advanced Analytics Program.",
    signer: "Dr. Alan Grant", date: "June 2026",
  },
  cover_letter: {
    name: "Jordan Rivera", contact: "jordan@email.com · +1 555 0134", date: "June 1, 2026",
    recipient: "Hiring Manager", company: "Nimbus Inc.",
    body: "I am excited to apply for the Senior Product Designer role at Nimbus. With over eight years of experience shaping digital products, I bring a track record of turning complex problems into elegant, usable solutions.\n\nIn my current role I led the redesign of a platform used by millions, improving engagement and reducing support tickets. I would love to bring that same impact to your team.\n\nThank you for your consideration. I look forward to speaking with you.",
  },
  report: {
    title: "Q2 2026 Performance Report", subtitle: "Growth, retention & roadmap", author: "Analytics Team", date: "July 2026", logo: "",
    sections: [
      { heading: "Executive Summary", content: "Revenue grew 24% quarter-over-quarter, driven by strong adoption of the new export tools." },
      { heading: "Key Metrics", content: "Active users up 18%.\nChurn down to 3.1%.\nNPS improved to 62." },
      { heading: "Next Quarter", content: "Focus on international expansion and mobile experience improvements." },
    ],
  },
};

const TEMPLATES = [
  { key: "invoice", label: "Invoice", testid: "template-select-invoice" },
  { key: "resume", label: "Resume", testid: "template-select-resume" },
  { key: "certificate", label: "Certificate", testid: "template-select-certificate" },
  { key: "cover_letter", label: "Cover Letter", testid: "template-select-cover_letter" },
  { key: "report", label: "Report", testid: "template-select-report" },
];

// Stable keys for editable list rows so React doesn't lose input state on add/remove/reorder.
const KEYED_ARRAYS = ["items", "experience", "education", "sections"];
let _kseq = 0;
const seedKeys = (d) => {
  const out = { ...d };
  KEYED_ARRAYS.forEach((a) => {
    if (Array.isArray(out[a])) out[a] = out[a].map((it) => ({ ...it, _k: ++_kseq }));
  });
  return out;
};
const stripKeys = (d) => {
  const out = { ...d };
  KEYED_ARRAYS.forEach((a) => {
    if (Array.isArray(out[a])) out[a] = out[a].map(({ _k, ...rest }) => rest);
  });
  return out;
};

export default function TemplatesTab({ initial }) {
  const [type, setType] = useState(initial?.template_type || "invoice");
  const [data, setData] = useState(seedKeys(initial?.data || DEMO.invoice));
  const [branding, setBranding] = useState({ watermark: initial?.watermark || "", brand_color: initial?.brand_color || "" });
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();
  const cost = opCost(user, `template:${type}`);

  const pick = (k) => { setType(k); setData(seedKeys(DEMO[k])); };
  const upd = (field, val) => setData((d) => ({ ...d, [field]: val }));
  const updItem = (arr, i, field, val) =>
    setData((d) => ({ ...d, [arr]: d[arr].map((it, idx) => (idx === i ? { ...it, [field]: val } : it)) }));
  const addItem = (arr, blank) => setData((d) => ({ ...d, [arr]: [...(d[arr] || []), { ...blank, _k: ++_kseq }] }));
  const rmItem = (arr, i) => setData((d) => ({ ...d, [arr]: d[arr].filter((_, idx) => idx !== i) }));

  const generate = async () => {
    setLoading(true);
    try {
      const title = type === "invoice" ? (data.invoice_number || "Invoice")
        : type === "resume" ? (data.name || "Resume")
        : type === "certificate" ? `Certificate - ${data.recipient}`
        : type === "cover_letter" ? `Cover Letter - ${data.name}`
        : (data.title || "Report");
      const res = await api.post("/pdf/template", { template_type: type, data: stripKeys(data), title, ...branding });
      setStage(res.data);
    } catch (e) {
      if (handlePdfError(e) === "no-credits") navigate("/buy-credits");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ToolIntro
        Icon={LayoutTemplate}
        title="Fill-in Templates"
        desc="Invoices, resumes, certificates, cover letters and reports. Fill the form on the left, preview live on the right, and export a designed PDF."
        accepts="Form inputs"
        tier={tierName(`template:${type}`)}
        costLabel={creditLabel(cost)}
      />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex bg-slate-100 rounded-full p-1 flex-wrap">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => pick(t.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                type === t.key ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <Button
          data-testid="generate-pdf-button"
          onClick={generate}
          disabled={loading || !online}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Create preview
        </Button>
      </div>

      <BrandingBar value={branding} onChange={setBranding} />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 max-h-[640px] overflow-y-auto">
          {type === "invoice" && <InvoiceForm data={data} upd={upd} updItem={updItem} addItem={addItem} rmItem={rmItem} />}
          {type === "resume" && <ResumeForm data={data} upd={upd} updItem={updItem} addItem={addItem} rmItem={rmItem} />}
          {type === "certificate" && <CertificateForm data={data} upd={upd} />}
          {type === "cover_letter" && <CoverLetterForm data={data} upd={upd} />}
          {type === "report" && <ReportForm data={data} upd={upd} updItem={updItem} addItem={addItem} rmItem={rmItem} />}
        </div>

        <div className="bg-slate-100 rounded-2xl border border-slate-200 p-6 flex justify-center items-start overflow-auto max-h-[640px]">
          <div className="bg-white paper-shadow w-full max-w-[520px] p-8 text-[11px] leading-relaxed text-slate-800">
            {type === "invoice" && <InvoicePreview d={data} />}
            {type === "resume" && <ResumePreview d={data} />}
            {type === "certificate" && <CertificatePreview d={data} />}
            {type === "cover_letter" && <CoverLetterPreview d={data} />}
            {type === "report" && <ReportPreview d={data} />}
          </div>
        </div>
      </div>

      {stage && <PreviewUnlock stage={stage} onClose={() => setStage(null)} onUnlocked={() => setStage(null)} />}
    </div>
  );
}

const Field = ({ label, ...props }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">{label}</Label>
    <Input className="bg-white" {...props} />
  </div>
);

function LogoUpload({ data, upd }) {
  const ref = useRef(null);
  const onFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (f.size > 1.5 * 1024 * 1024) { toast.error("Logo too large (max 1.5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => upd("logo", reader.result);
    reader.readAsDataURL(f);
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">Logo (optional)</Label>
      {data.logo ? (
        <div className="flex items-center gap-3">
          <img src={data.logo} alt="logo" className="h-12 max-w-[120px] object-contain border border-slate-200 rounded p-1 bg-white" />
          <button data-testid="logo-remove" onClick={() => upd("logo", "")} className="text-slate-400 hover:text-red-500 flex items-center gap-1 text-sm">
            <X className="h-4 w-4" /> Remove
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="logo-upload"
          onClick={() => ref.current?.click()}
          className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 border border-dashed border-slate-300 rounded-lg px-3 py-2 w-full justify-center hover:border-indigo-400"
        >
          <ImagePlus className="h-4 w-4" /> Upload logo
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
    </div>
  );
}

function InvoiceForm({ data, upd, updItem, addItem, rmItem }) {
  return (
    <>
      <LogoUpload data={data} upd={upd} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Invoice #" value={data.invoice_number} onChange={(e) => upd("invoice_number", e.target.value)} />
        <Field label="Currency" value={data.currency} onChange={(e) => upd("currency", e.target.value)} />
      </div>
      <Field label="From (name)" value={data.from_name} onChange={(e) => upd("from_name", e.target.value)} />
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">From (details)</Label>
        <Textarea className="bg-white" value={data.from_details} onChange={(e) => upd("from_details", e.target.value)} />
      </div>
      <Field label="Bill to (name)" value={data.to_name} onChange={(e) => upd("to_name", e.target.value)} />
      <Field label="Bill to (details)" value={data.to_details} onChange={(e) => upd("to_details", e.target.value)} />
      <div className="grid grid-cols-3 gap-3">
        <Field label="Date" value={data.date} onChange={(e) => upd("date", e.target.value)} />
        <Field label="Due" value={data.due_date} onChange={(e) => upd("due_date", e.target.value)} />
        <Field label="Tax %" type="number" value={data.tax_rate} onChange={(e) => upd("tax_rate", e.target.value)} />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Line items</Label>
        <div className="space-y-2 mt-1">
          {data.items.map((it, i) => (
            <div key={it._k ?? i} className="flex gap-2 items-center">
              <Input className="bg-white flex-1" placeholder="Description" value={it.description} onChange={(e) => updItem("items", i, "description", e.target.value)} />
              <Input className="bg-white w-16" type="number" placeholder="Qty" value={it.qty} onChange={(e) => updItem("items", i, "qty", e.target.value)} />
              <Input className="bg-white w-24" type="number" placeholder="Price" value={it.price} onChange={(e) => updItem("items", i, "price", e.target.value)} />
              <button onClick={() => rmItem("items", i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <button data-testid="invoice-add-item" onClick={() => addItem("items", { description: "", qty: 1, price: 0 })} className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
          <Plus className="h-4 w-4" /> Add item
        </button>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Notes</Label>
        <Textarea className="bg-white" value={data.notes} onChange={(e) => upd("notes", e.target.value)} />
      </div>
    </>
  );
}

function ResumeForm({ data, upd, updItem, addItem, rmItem }) {
  return (
    <>
      <Field label="Name" value={data.name} onChange={(e) => upd("name", e.target.value)} />
      <Field label="Headline" value={data.headline} onChange={(e) => upd("headline", e.target.value)} />
      <Field label="Contact" value={data.contact} onChange={(e) => upd("contact", e.target.value)} />
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Summary</Label>
        <Textarea className="bg-white" value={data.summary} onChange={(e) => upd("summary", e.target.value)} />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Experience</Label>
        <div className="space-y-3 mt-1">
          {data.experience.map((e, i) => (
            <div key={e._k ?? i} className="border border-slate-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between">
                <Input className="bg-white flex-1 mr-2" placeholder="Role" value={e.role} onChange={(ev) => updItem("experience", i, "role", ev.target.value)} />
                <button onClick={() => rmItem("experience", i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Input className="bg-white" placeholder="Company" value={e.company} onChange={(ev) => updItem("experience", i, "company", ev.target.value)} />
              <Input className="bg-white" placeholder="Period" value={e.period} onChange={(ev) => updItem("experience", i, "period", ev.target.value)} />
              <Textarea className="bg-white" placeholder="Summary" value={e.summary} onChange={(ev) => updItem("experience", i, "summary", ev.target.value)} />
            </div>
          ))}
        </div>
        <button data-testid="resume-add-exp" onClick={() => addItem("experience", { role: "", company: "", period: "", summary: "" })} className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
          <Plus className="h-4 w-4" /> Add experience
        </button>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Education</Label>
        <div className="space-y-3 mt-1">
          {data.education.map((e, i) => (
            <div key={e._k ?? i} className="border border-slate-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between">
                <Input className="bg-white flex-1 mr-2" placeholder="Degree" value={e.degree} onChange={(ev) => updItem("education", i, "degree", ev.target.value)} />
                <button onClick={() => rmItem("education", i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Input className="bg-white" placeholder="School" value={e.school} onChange={(ev) => updItem("education", i, "school", ev.target.value)} />
              <Input className="bg-white" placeholder="Period" value={e.period} onChange={(ev) => updItem("education", i, "period", ev.target.value)} />
            </div>
          ))}
        </div>
        <button data-testid="resume-add-edu" onClick={() => addItem("education", { degree: "", school: "", period: "" })} className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
          <Plus className="h-4 w-4" /> Add education
        </button>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Skills</Label>
        <Textarea className="bg-white" value={data.skills} onChange={(e) => upd("skills", e.target.value)} />
      </div>
    </>
  );
}

function CertificateForm({ data, upd }) {
  return (
    <>
      <LogoUpload data={data} upd={upd} />
      <Field label="Type (e.g. Achievement)" value={data.type} onChange={(e) => upd("type", e.target.value)} />
      <Field label="Recipient" value={data.recipient} onChange={(e) => upd("recipient", e.target.value)} />
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Description</Label>
        <Textarea className="bg-white" value={data.description} onChange={(e) => upd("description", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Signed by" value={data.signer} onChange={(e) => upd("signer", e.target.value)} />
        <Field label="Date" value={data.date} onChange={(e) => upd("date", e.target.value)} />
      </div>
    </>
  );
}

function CoverLetterForm({ data, upd }) {
  return (
    <>
      <Field label="Your name" value={data.name} onChange={(e) => upd("name", e.target.value)} />
      <Field label="Your contact" value={data.contact} onChange={(e) => upd("contact", e.target.value)} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Recipient" value={data.recipient} onChange={(e) => upd("recipient", e.target.value)} />
        <Field label="Company" value={data.company} onChange={(e) => upd("company", e.target.value)} />
      </div>
      <Field label="Date" value={data.date} onChange={(e) => upd("date", e.target.value)} />
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Letter body</Label>
        <Textarea className="bg-white min-h-[220px]" value={data.body} onChange={(e) => upd("body", e.target.value)} />
      </div>
    </>
  );
}

function ReportForm({ data, upd, updItem, addItem, rmItem }) {
  return (
    <>
      <LogoUpload data={data} upd={upd} />
      <Field label="Title" value={data.title} onChange={(e) => upd("title", e.target.value)} />
      <Field label="Subtitle" value={data.subtitle} onChange={(e) => upd("subtitle", e.target.value)} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Author" value={data.author} onChange={(e) => upd("author", e.target.value)} />
        <Field label="Date" value={data.date} onChange={(e) => upd("date", e.target.value)} />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Sections</Label>
        <div className="space-y-3 mt-1">
          {data.sections.map((s, i) => (
            <div key={s._k ?? i} className="border border-slate-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between">
                <Input className="bg-white flex-1 mr-2" placeholder="Heading" value={s.heading} onChange={(ev) => updItem("sections", i, "heading", ev.target.value)} />
                <button onClick={() => rmItem("sections", i)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Textarea className="bg-white" placeholder="Content" value={s.content} onChange={(ev) => updItem("sections", i, "content", ev.target.value)} />
            </div>
          ))}
        </div>
        <button data-testid="report-add-section" onClick={() => addItem("sections", { heading: "", content: "" })} className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
          <Plus className="h-4 w-4" /> Add section
        </button>
      </div>
    </>
  );
}

// ---------- Previews ----------
function InvoicePreview({ d }) {
  const items = d.items || [];
  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  const tax = (subtotal * (Number(d.tax_rate) || 0)) / 100;
  return (
    <div>
      <div className="flex justify-between items-start">
        <div>
          {d.logo && <img src={d.logo} alt="logo" className="h-10 mb-1 object-contain" />}
          <div className="text-indigo-600 font-bold text-xl">INVOICE</div><div className="text-slate-400">#{d.invoice_number}</div>
        </div>
        <div className="text-right"><div className="font-semibold">{d.from_name}</div><div className="text-slate-400 whitespace-pre-line">{d.from_details}</div></div>
      </div>
      <div className="border-t-2 border-indigo-600 my-2" />
      <div className="flex justify-between">
        <div><span className="font-semibold">Bill To</span><br />{d.to_name}<br /><span className="text-slate-400">{d.to_details}</span></div>
        <div className="text-right"><span className="font-semibold">Date</span> {d.date}<br /><span className="font-semibold">Due</span> {d.due_date}</div>
      </div>
      <table className="w-full mt-3 border-collapse">
        <thead><tr className="bg-slate-100"><th className="border border-slate-200 p-1 text-left">Description</th><th className="border border-slate-200 p-1">Qty</th><th className="border border-slate-200 p-1">Price</th><th className="border border-slate-200 p-1">Amt</th></tr></thead>
        <tbody>{items.map((it, i) => (
          <tr key={i}><td className="border border-slate-200 p-1">{it.description}</td><td className="border border-slate-200 p-1 text-right">{it.qty}</td><td className="border border-slate-200 p-1 text-right">{Number(it.price).toFixed(2)}</td><td className="border border-slate-200 p-1 text-right">{((Number(it.qty)||0)*(Number(it.price)||0)).toFixed(2)}</td></tr>
        ))}</tbody>
      </table>
      <div className="w-40 ml-auto mt-2">
        <div className="flex justify-between"><span>Subtotal</span><span>{d.currency}{subtotal.toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Tax</span><span>{d.currency}{tax.toFixed(2)}</span></div>
        <div className="flex justify-between font-bold border-t border-slate-800 mt-1 pt-1"><span>Total</span><span>{d.currency}{(subtotal + tax).toFixed(2)}</span></div>
      </div>
      <p className="text-slate-400 mt-3">{d.notes}</p>
    </div>
  );
}

function ResumePreview({ d }) {
  const Sec = ({ t, children }) => <div className="mt-2"><div className="text-indigo-600 font-bold border-b border-slate-200 pb-0.5">{t}</div>{children}</div>;
  return (
    <div>
      <div className="text-center"><div className="font-bold text-lg">{d.name}</div><div className="text-indigo-600 font-semibold">{d.headline}</div><div className="text-slate-400">{d.contact}</div></div>
      {d.summary && <Sec t="Summary"><p>{d.summary}</p></Sec>}
      <Sec t="Experience">{(d.experience||[]).map((e, i) => (<div key={i} className="mt-1"><span className="font-semibold">{e.role}</span> — {e.company} <span className="float-right text-slate-400">{e.period}</span><div className="text-slate-500">{e.summary}</div></div>))}</Sec>
      <Sec t="Education">{(d.education||[]).map((e, i) => (<div key={i}><span className="font-semibold">{e.degree}</span> — {e.school} <span className="float-right text-slate-400">{e.period}</span></div>))}</Sec>
      {d.skills && <Sec t="Skills"><p>{d.skills}</p></Sec>}
    </div>
  );
}

function CertificatePreview({ d }) {
  return (
    <div className="border-4 border-double border-indigo-600 p-6 text-center">
      {d.logo && <img src={d.logo} alt="logo" className="h-10 mx-auto mb-2 object-contain" />}
      <div className="tracking-widest text-slate-400">CERTIFICATE OF {(d.type||"").toUpperCase()}</div>
      <div className="mt-4 text-slate-500">This is proudly presented to</div>
      <div className="text-2xl font-bold text-indigo-600 my-2">{d.recipient}</div>
      <p className="text-slate-600 max-w-xs mx-auto">{d.description}</p>
      <div className="flex justify-around mt-8"><div className="border-t border-slate-800 pt-1 w-24">{d.signer}</div><div className="border-t border-slate-800 pt-1 w-24">{d.date}</div></div>
    </div>
  );
}

function CoverLetterPreview({ d }) {
  return (
    <div>
      <div className="mb-3"><span className="font-bold text-sm">{d.name}</span><br /><span className="text-slate-400">{d.contact}</span></div>
      <div className="text-slate-400">{d.date}</div>
      <div className="my-2"><span className="font-semibold">{d.recipient}</span><br /><span className="text-slate-400">{d.company}</span></div>
      <p>Dear {d.recipient},</p>
      {(d.body || "").split("\n\n").map((p, i) => <p key={i} className="mt-1">{p}</p>)}
      <p className="mt-3">Sincerely,<br /><span className="font-semibold">{d.name}</span></p>
    </div>
  );
}

function ReportPreview({ d }) {
  return (
    <div>
      <div className="border-b-2 border-indigo-600 pb-2 mb-2">
        {d.logo && <img src={d.logo} alt="logo" className="h-8 mb-1 object-contain" />}
        <div className="font-bold text-base">{d.title}</div>
        <div className="text-slate-500">{d.subtitle}</div>
        <div className="text-slate-400">{d.author} · {d.date}</div>
      </div>
      {(d.sections || []).map((s, i) => (
        <div key={i} className="mt-2">
          <div className="text-indigo-600 font-bold">{s.heading}</div>
          {(s.content || "").split("\n\n").map((p, j) => <p key={j}>{p}</p>)}
        </div>
      ))}
    </div>
  );
}
